import { useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined security questions
const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What is your mother's maiden name?",
  "What city were you born in?",
  "What is your favorite food?",
  "What was the name of your first school?",
  "What is your favorite color?",
  "What is your father's middle name?"
];

export default function LoginPage({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: username, 2: answer, 3: new password
  
  // Check if redirected due to token expiration
  const urlParams = new URLSearchParams(window.location.search);
  const tokenExpired = urlParams.get('expired') === 'true';
  
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    full_name: "",
    role: "user",
    department: "",
    security_question: "",
    security_answer: ""
  });
  
  const [forgotPasswordData, setForgotPasswordData] = useState({
    username: "",
    security_question: "",
    security_answer: "",
    new_password: "",
    confirm_password: ""
  });
  
  const [error, setError] = useState(tokenExpired ? "Your session has expired. Please log in again." : "");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const payload = isRegister 
        ? formData 
        : { username: formData.username, password: formData.password };
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      onLogin(response.data.token, response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleForgotPasswordChange = (e) => {
    setForgotPasswordData({ ...forgotPasswordData, [e.target.name]: e.target.value });
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      if (forgotPasswordStep === 1) {
        // Step 1: Get security question
        const response = await axios.get(`${API}/auth/security-question/${forgotPasswordData.username}`);
        setForgotPasswordData({
          ...forgotPasswordData,
          security_question: response.data.security_question
        });
        setForgotPasswordStep(2);
      } else if (forgotPasswordStep === 2) {
        // Step 2: Verify answer and set new password
        if (forgotPasswordData.new_password !== forgotPasswordData.confirm_password) {
          setError("Passwords do not match");
          setLoading(false);
          return;
        }

        await axios.post(`${API}/auth/reset-password`, {
          username: forgotPasswordData.username,
          security_answer: forgotPasswordData.security_answer,
          new_password: forgotPasswordData.new_password
        });

        setSuccess("Password reset successful! You can now login with your new password.");
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotPasswordStep(1);
          setForgotPasswordData({
            username: "",
            security_question: "",
            security_answer: "",
            new_password: "",
            confirm_password: ""
          });
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setForgotPasswordData({
      username: "",
      security_question: "",
      security_answer: "",
      new_password: "",
      confirm_password: ""
    });
    setError("");
    setSuccess("");
  };

  return (
    <div className="flex h-screen">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_order-system-test-1/artifacts/mggo7vv7_05_3_d0261bf8-15a4-4cd8-9dbf-0a38c4e7124c_765x.webp"
          alt="Eurospa Terry Towels"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/30 to-transparent" />
        <div className="absolute bottom-10 left-10 text-white drop-shadow-lg">
          <h2 className="text-3xl font-bold mb-2">Eurospa Terry Towels Pvt Ltd</h2>
          <p className="text-lg">Premium Quality Manufacturing</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="font-heading font-bold text-4xl text-primary mb-2" data-testid="login-title">
              Order Factory
            </h1>
            <p className="text-muted-foreground">
              {isRegister ? "Create your account" : "Sign in to your account"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
            {isRegister && (
              <>
                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium mb-2">
                    Full Name
                  </label>
                  <input
                    id="full_name"
                    name="full_name"
                    type="text"
                    required={isRegister}
                    value={formData.full_name}
                    onChange={handleChange}
                    data-testid="full-name-input"
                    className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label htmlFor="department" className="block text-sm font-medium mb-2">
                    Department
                  </label>
                  <select
                    id="department"
                    name="department"
                    required={isRegister}
                    value={formData.department}
                    onChange={handleChange}
                    data-testid="department-select"
                    className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select Department</option>
                    <option value="admin">Admin (Full Access)</option>
                    <option value="accounts">Accounts Department</option>
                    <option value="weaving">Weaving Department</option>
                    <option value="dyeing">Dyeing Department</option>
                    <option value="stitching">Stitching / Accessories Department</option>
                    <option value="marketing">Marketing Department</option>
                    <option value="maintenance">Maintenance Department</option>
                    <option value="color_store">Color Store Department</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="role" className="block text-sm font-medium mb-2">
                    Role
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    data-testid="role-select"
                    className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="security_question" className="block text-sm font-medium mb-2">
                    Security Question
                  </label>
                  <select
                    id="security_question"
                    name="security_question"
                    required={isRegister}
                    value={formData.security_question}
                    onChange={handleChange}
                    data-testid="security-question-select"
                    className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select a security question</option>
                    {SECURITY_QUESTIONS.map((q, idx) => (
                      <option key={idx} value={q}>{q}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="security_answer" className="block text-sm font-medium mb-2">
                    Security Answer
                  </label>
                  <input
                    id="security_answer"
                    name="security_answer"
                    type="text"
                    required={isRegister}
                    value={formData.security_answer}
                    onChange={handleChange}
                    data-testid="security-answer-input"
                    className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Enter your answer"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This will be used for password recovery
                  </p>
                </div>
              </>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium mb-2">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                data-testid="username-input"
                className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                data-testid="password-input"
                className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Enter your password"
              />
            </div>

            {!isRegister && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setError("");
                  }}
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm" data-testid="error-message">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-button"
              className="w-full py-3 bg-primary text-primary-foreground font-medium rounded-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Please wait..." : (isRegister ? "Create Account" : "Sign In")}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsRegister(!isRegister);
                setError("");
              }}
              data-testid="toggle-auth-mode"
              className="text-sm text-primary hover:underline"
            >
              {isRegister
                ? "Already have an account? Sign in"
                : "Don't have an account? Create one"}
            </button>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-sm p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Reset Password</h2>
              <button
                onClick={resetForgotPassword}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              {forgotPasswordStep === 1 && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    name="username"
                    required
                    value={forgotPasswordData.username}
                    onChange={handleForgotPasswordChange}
                    className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Enter your username"
                  />
                </div>
              )}

              {forgotPasswordStep === 2 && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-4">
                    <p className="text-sm font-medium text-blue-900 mb-1">
                      Security Question:
                    </p>
                    <p className="text-sm text-blue-800">
                      {forgotPasswordData.security_question}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Your Answer
                    </label>
                    <input
                      type="text"
                      name="security_answer"
                      required
                      value={forgotPasswordData.security_answer}
                      onChange={handleForgotPasswordChange}
                      className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Enter your answer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      name="new_password"
                      required
                      value={forgotPasswordData.new_password}
                      onChange={handleForgotPasswordChange}
                      className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Enter new password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      name="confirm_password"
                      required
                      value={forgotPasswordData.confirm_password}
                      onChange={handleForgotPasswordChange}
                      className="w-full px-4 py-2.5 bg-background border border-input rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Confirm new password"
                    />
                  </div>
                </>
              )}

              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-sm">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                  <p className="text-sm text-green-800">{success}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={resetForgotPassword}
                  className="flex-1 py-2.5 border border-input rounded-sm hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50"
                >
                  {loading ? "Please wait..." : (forgotPasswordStep === 1 ? "Next" : "Reset Password")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
